import React, { FC, useCallback, useEffect, useState } from 'react'
import { Row, Col, Divider, Layout, Button, Spin } from 'antd'
import cx from 'classnames'
import { initialize, isSubmitting } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'

// assets
import { ReactComponent as DuplicateIcon } from '../../assets/icons/BaseIcons/duplicate-1-icon.svg'
import useBackUrl from '../../hooks/useBackUrl'

// components
import FormSettingsModal from '../../components/modals/FormSettingsModal'
import TemplateModal from '../../components/modals/TemplateModal'
import FormGeneralSettingsForm from './components/FormGeneralSettingsForm'
import ProceduresSettingsForm from '../../components/forms/ProceduresSettingsForm'
import ConfirmationModal from '../../components/modals/ConfirmationModal'

// reducers
import { RootState } from '../../reducers'
import { FORM_EDITED } from '../../reducers/form/formTypes'
import { getDatabases, getFormTemplate, getProcedures, getQueues } from '../../reducers/form/formActions'

// utils
import { deleteReq, patchReq, postReq } from '../../utils/request'
import { FORM, FORM_TEMPLATE_STATUSES, FORM_TEMPLATE_TYPES } from '../../utils/enums'

// types
import { ITemplate, ITemplateCreateModal, IForm, IUpdateForm } from '../../types/interfaces'

const { Sider, Content } = Layout

type Props = {}

const FormSettingsPage: FC<Props> = () => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { templateId } = useParams<{ templateId?: string }>()
	const navigate = useNavigate()
	const [isConfirmModalShowed, setConfirmModalShowed] = useState(false)
	const [isDuplicatedModalShowed, setDuplicatedModalShowed] = useState(false)
	const [isTemplateModalShowed, setTemplateModalShowed] = useState(false)
	const [processing, setProcessing] = useState(false)
	const formSubmitting = useSelector((state: RootState) => isSubmitting(FORM.FORM_GENERAL_SETTINGS)(state))
	const [backUrlLink] = useBackUrl()
	const data = useSelector((state: RootState) => state.formTemplate)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)

	const initFetch = useCallback(
		async (formTemplateId?: string) => {
			const res = await dispatch(getFormTemplate(formTemplateId))
			const backupForm: IForm = { ...res.data?.backup, queue: res.data?.backup?.queue?.name }
			dispatch(getProcedures(backupForm?.database))
			dispatch(initialize(FORM.FORM_GENERAL_SETTINGS, backupForm))
			dispatch(initialize(FORM.PROCEDURES_SETTINGS, backupForm))
		},
		[dispatch]
	)

	useEffect(() => {
		dispatch(getQueues())
		dispatch(getDatabases())
		initFetch(templateId)
	}, [dispatch, initFetch, templateId])

	// update from template properties
	const handleSubmit = async (formData: any) => {
		try {
			const form: IUpdateForm = {
				formName: formData?.name,
				formDatabase: formData?.database,
				formQueue: formData?.queue,
				formType: formData?.type,
				formAutoDialingDelay: Number(formData?.autoDialingDelay),
				formUsesAutoDialing: formData?.usesAutoDialing,
				formAutoDialsBestRecord: formData?.autoDialsBestRecord,
				formUsesAutoHangup: formData?.usesAutoHangup,
				formAutoHangupDelay: Number(formData?.autoHangupDelay)
			}
			// clear procedures when database change to different
			if (formData?.database !== data.formTemplate.data?.backup.database) {
				form.formInputStoredProcedure = ''
				form.formOutputStoredProcedureBeforeSave = ''
				form.formOutputStoredProcedureAfterSave = ''
			} else {
				if (typeof formData?.inputStoredProcedure === 'string') {
					form.formInputStoredProcedure = formData?.inputStoredProcedure
				}
				if (typeof formData?.outputStoredProcedureBeforeSave === 'string') {
					form.formOutputStoredProcedureBeforeSave = formData?.outputStoredProcedureBeforeSave
				}
				if (typeof formData?.outputStoredProcedureAfterSave === 'string') {
					form.formOutputStoredProcedureAfterSave = formData?.outputStoredProcedureAfterSave
				}
			}
			if (!formData?.type?.includes(FORM_TEMPLATE_TYPES.INBOUND)) {
				form.formSearchStoredProcedure = ''
				form.formInsertStoredProcedure = ''
				form.formRetrieveStoredProcedure = ''
			} else {
				if (typeof formData?.searchStoredProcedure === 'string') {
					form.formSearchStoredProcedure = formData?.searchStoredProcedure
				}
				if (typeof formData?.retrieveStoredProcedure === 'string') {
					form.formRetrieveStoredProcedure = formData?.retrieveStoredProcedure
				}
				if (typeof formData?.insertStoredProcedure === 'string') {
					form.formInsertStoredProcedure = formData?.insertStoredProcedure
				}
			}
			await patchReq(`/api/admin/forms/${templateId}`, undefined, form)
			const edited = true
			dispatch({ type: FORM_EDITED, edited })
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		} finally {
			await initFetch(templateId)
		}
	}

	const onDeleteConfirm = async () => {
		try {
			setProcessing(true)
			await deleteReq(`/api/admin/forms/${templateId}`, undefined)
			setConfirmModalShowed(false)
			setProcessing(false)
			if (backUrlLink) {
				navigate(backUrlLink)
			}
		} catch (err: any) {
			setProcessing(false)
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	const duplicateFormTemplate = async (formData: IForm) => {
		try {
			setProcessing(true)
			const duplicatedForm: IUpdateForm = {
				formName: formData.name,
				formDatabase: formData.database,
				formQueue: `${formData.queue}`,
				formType: formData.type
			}
			const res = await postReq(`/api/admin/forms/${templateId}/duplicate`, undefined, duplicatedForm)
			setProcessing(false)
			setDuplicatedModalShowed(false)
			navigate(t(`path:/settings/${res?.data?.data?.newFormID}?backUrl=${btoa(t('paths:index'))}`) as string)
		} catch (err: any) {
			setProcessing(false)
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	const createNewFormTemplate = async (formTemplateData: ITemplateCreateModal) => {
		try {
			setProcessing(true)
			const formTemplate: ITemplate = {
				name: formTemplateData?.name,
				description: formTemplateData?.description,
				formName: data.formTemplate.data?.name,
				formDatabase: data.formTemplate.data?.database,
				formQueue: data.formTemplate.data?.queue?.name,
				formType: formTemplateData?.type
			}
			const res = await postReq(`/api/admin/forms/${templateId}/template`, undefined, formTemplate)
			setProcessing(false)
			setTemplateModalShowed(false)
			navigate(t(`path:/template-settings/${res?.data?.data?.templateID}?backUrl=${btoa(t('paths:/templates'))}`) as string)
		} catch (err: any) {
			setProcessing(false)
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	const editClass = cx({
		'hide-element': !formSubmitting || !data?.formTemplate?.isLoading
	})

	return (
		<>
			<div className={`full-w-placeholder ${editClass}`}>
				<Spin size='large' />
			</div>
			<Sider width={310} className={'settings-sider'} />
			<Content className={'settings-contect'}>
				<div className={'template-setting-form'}>
					<Row gutter={16}>
						<Col className='gutter-row' span={12}>
							<Button
								className={'unfilled-btn w-45 h-10'}
								icon={<DuplicateIcon width={20} height={20} />}
								onClick={() => {
									setDuplicatedModalShowed(true)
									dispatch(
										initialize(FORM.GENERALE_TEMPLATE_ATTRIBUTES, {
											...data?.formTemplate.data?.backup,
											queue: data?.formTemplate.data?.backup?.queue?.name
										})
									)
								}}
								disabled={disable}
							>
								{t('loc:Duplicate form')}
							</Button>
							<Button
								id='unfilled-btn-setting'
								className={'unfilled-btn w-45 h-10'}
								onClick={() => {
									setTemplateModalShowed(true)
									dispatch(initialize(FORM.FORM_TEMPLATE, { type: data.formTemplate.data?.backup.type }))
								}}
								disabled={disable}
							>
								{t('loc:Create Template')}
							</Button>
						</Col>
						<Col className='gutter-row' span={12}>
							<Button
								type='text'
								className={'btn-error-text-color w-45 h-10 float-right'}
								onClick={() => {
									setConfirmModalShowed(true)
								}}
								disabled={disable || !(data.formTemplate?.data?.backup?.status === FORM_TEMPLATE_STATUSES.ARCHIVED)}
							>
								{t('loc:Delete form')}
							</Button>
						</Col>
					</Row>
					<Divider />
					<Row gutter={16}>
						<Col className='gutter-row' span={12}>
							<h3>{t('loc:General')}</h3>
						</Col>
						<Col className='gutter-row' span={12}>
							<FormGeneralSettingsForm onSubmit={handleSubmit} />
						</Col>
					</Row>
					<Divider />
					<Row gutter={16}>
						<Col className='gutter-row' span={12}>
							<h3>{t('loc:Procedures')}</h3>
						</Col>
						<Col className='gutter-row' span={12}>
							<ProceduresSettingsForm
								inputName={'inputStoredProcedure'}
								outputBeforeName={'outputStoredProcedureBeforeSave'}
								outputAfterName={'outputStoredProcedureAfterSave'}
								onSubmit={handleSubmit}
								disable={disable}
							/>
						</Col>
					</Row>
				</div>
			</Content>
			<Sider width={310} className={'settings-sider'} />
			<ConfirmationModal
				className={'delete-form-modal'}
				width={400}
				isVisible={isConfirmModalShowed}
				title={t('loc:Delete form?')}
				message={t('loc: Form will be permanently deleted')}
				onConfirm={onDeleteConfirm}
				onCancel={() => {
					setConfirmModalShowed(false)
				}}
				cancelText={t('loc:Cancel')}
				onConfirmBtnTitle={t('loc:Delete form')}
				classNameBtn={'btn-error-background-color'}
				disabled={processing}
			/>
			<FormSettingsModal
				width={460}
				handleSubmit={duplicateFormTemplate}
				isVisible={isDuplicatedModalShowed}
				title={t('loc:Duplicate form')}
				onCancel={() => {
					setDuplicatedModalShowed(false)
				}}
				cancelText={t('loc:Back')}
				onConfirmBtnTitle={t('loc:Duplicate')}
				disabled={processing}
			/>
			<TemplateModal
				width={460}
				handleSubmit={createNewFormTemplate}
				isVisible={isTemplateModalShowed}
				title={t('loc:Create template')}
				onCancel={() => {
					setTemplateModalShowed(false)
				}}
				onConfirmBtnTitle={t('loc:Create template')}
				disabled={processing}
			/>
		</>
	)
}

export default FormSettingsPage
